import {RemoteConstantsModel} from "../models/remoteConstants.model";

export interface ImageLabel {
    label: string;
    orphan: boolean;     // not referenced by any pack
    explicit: boolean;   // user-set displayName, not derived
}

export interface ImageLabelResolver {
    labelFor(key: string): ImageLabel;
}

// Builds a resolver that turns image keys into human-readable labels.
// Cache the resolver per render via useMemo if you call labelFor on many
// keys — the precomputed lookup tables make the per-key call O(1).
//
// Priority chain:
//   1. user-set displayName on ImageMetadata
//   2. entity that owns this key via its imagePack (with level/variation suffix)
//   3. orphan key parsed as `<entityId>_<level>_<variation>`
//   4. bare entity id (whose own pack no longer references it — rare)
//   5. raw key fallback
export function makeImageLabelResolver(
    state: RemoteConstantsModel,
    getLocalizationValue: (id: string) => string,
): ImageLabelResolver {
    // Reverse-index: image key → owning entity + slot. First match wins
    // when the same key appears in multiple packs (unusual but possible).
    const ownerByKey = new Map<string, {entityId: string; level: number; variation: number}>();
    for (const [entityId, pack] of Object.entries(state.imagePacks ?? {})) {
        for (let level = 0; level < pack.length; level++) {
            for (let variation = 0; variation < pack[level].length; variation++) {
                const k = pack[level][variation];
                if (!ownerByKey.has(k)) ownerByKey.set(k, {entityId, level, variation});
            }
        }
    }

    const knownEntityIds = new Set<string>();
    state.buildings?.forEach(b => knownEntityIds.add(b.id));
    state.units?.forEach(u => knownEntityIds.add(u.id));
    state.tiles?.forEach(t => knownEntityIds.add(t.id));
    state.resources?.forEach(r => knownEntityIds.add(r.id));

    function derivedFor(entityId: string, level: number, variation: number): string {
        const name = getLocalizationValue(entityId);
        if (level === 0 && variation === 0) return name;
        return `${name} L${level}#${variation}`;
    }

    function labelFor(key: string): ImageLabel {
        const meta = state.images?.[key];
        const explicitName = (meta && typeof meta === 'object' ? meta.displayName : undefined);
        if (explicitName && explicitName.length > 0) {
            return {label: explicitName, orphan: !ownerByKey.has(key), explicit: true};
        }

        const ref = ownerByKey.get(key);
        if (ref) {
            return {label: derivedFor(ref.entityId, ref.level, ref.variation), orphan: false, explicit: false};
        }

        const m = key.match(/^(.+)_(\d+)_(\d+)$/);
        if (m) {
            const [, entityId, levelStr, varStr] = m;
            if (knownEntityIds.has(entityId)) {
                return {label: derivedFor(entityId, +levelStr, +varStr), orphan: true, explicit: false};
            }
        }

        if (knownEntityIds.has(key)) {
            return {label: getLocalizationValue(key), orphan: true, explicit: false};
        }

        return {label: key, orphan: true, explicit: false};
    }

    return {labelFor};
}
